import { AUDIT_RESOURCE_TYPE, flattenForAudit } from "../constants/auditFields";
import { postAuditBulk, postInitialAudit } from "../services/network";

type AuditSetters = {
  setAuditedFieldPaths?: (updater: any) => void;
  setFieldAuditHistoryCache?: (updater: any) => void;
  setPendingAuditEdits?: (value: any) => void;
  setIsFirstEverAudit?: (value: boolean) => void;
};

type FlushPendingAuditEditsParams = AuditSetters & {
  loanId: any;
  tempData?: any;
  isFirstEverAudit?: boolean;
  pendingAuditEdits?: Record<string, { value: any; comment: string }>;
  decodedTokenData?: any;
  realmHeaders?: any;
};

const resolveUpdatedBy = (decodedTokenData: any) =>
  decodedTokenData?.name || decodedTokenData?.email || decodedTokenData?.user_id || "";

// Records a save's field edits with the external audit service — either
// seeding history for every field (a loan's very first-ever save) or
// bulk-recording just the edited fields (every save after that). Shared by
// every save flow that needs to post audit entries, so the branching logic
// and network-call shape live in exactly one place.
export const flushPendingAuditEdits = async ({
  loanId,
  tempData,
  isFirstEverAudit,
  pendingAuditEdits,
  decodedTokenData,
  realmHeaders,
  setAuditedFieldPaths,
  setFieldAuditHistoryCache,
  setPendingAuditEdits,
  setIsFirstEverAudit,
}: FlushPendingAuditEditsParams) => {
  try {
    if (isFirstEverAudit) {
      const flattened = flattenForAudit(tempData);
      await postInitialAudit(loanId ?? "", flattened, realmHeaders);
      setAuditedFieldPaths?.(Object.keys(flattened));
      setIsFirstEverAudit?.(false);
      setPendingAuditEdits?.({});
      return;
    }

    if (!pendingAuditEdits || !Object.keys(pendingAuditEdits).length) {
      return;
    }

    const updatedBy = resolveUpdatedBy(decodedTokenData);
    const editedPaths = Object.keys(pendingAuditEdits);
    const entries = editedPaths.map((field) => ({
      field,
      value: pendingAuditEdits[field]?.value,
      comment: pendingAuditEdits[field]?.comment,
      updatedBy,
    }));

    await postAuditBulk(
      [{ resourceId: loanId ?? "", resourceType: AUDIT_RESOURCE_TYPE, data: entries }],
      realmHeaders,
    );

    setAuditedFieldPaths?.((prev: string[]) =>
      Array.from(new Set([...(prev ?? []), ...editedPaths])),
    );
    setFieldAuditHistoryCache?.((prev: Record<string, any[]>) => {
      const next = { ...prev };
      editedPaths.forEach((path) => delete next[path]);
      return next;
    });
    setPendingAuditEdits?.({});
  } catch (auditErr) {
    console.error(auditErr);
  }
};
